<!-- Loading Skeleton -->
@if (isLoading()) {
	<div class="min-h-screen bg-white">
		<!-- Hero Skeleton -->
		<div class="relative h-[60vh] bg-charcoal/5 animate-pulse">
			<div class="absolute inset-0 bg-gradient-to-t from-charcoal/80 to-transparent"></div>
			<div class="absolute bottom-0 left-0 right-0 p-8 md:p-16">
				<div class="max-w-4xl mx-auto space-y-4">
					<div class="h-4 bg-white/20 rounded w-32"></div>
					<div class="h-12 bg-white/20 rounded w-3/4"></div>
					<div class="flex gap-4">
						<div class="h-4 bg-white/20 rounded w-24"></div>
						<div class="h-4 bg-white/20 rounded w-24"></div>
					</div>
				</div>
			</div>
		</div>

		<!-- Content Skeleton -->
		<div class="max-w-4xl mx-auto px-8 md:px-16 py-12">
			<div class="space-y-4 animate-pulse">
				<div class="h-4 bg-charcoal/10 rounded w-full"></div>
				<div class="h-4 bg-charcoal/10 rounded w-full"></div>
				<div class="h-4 bg-charcoal/10 rounded w-3/4"></div>
				<div class="h-4 bg-charcoal/10 rounded w-full"></div>
				<div class="h-4 bg-charcoal/10 rounded w-5/6"></div>
			</div>
		</div>
	</div>
}

<!-- Error State -->
@else if (errorMessage()) {
	<div class="min-h-screen flex items-center justify-center bg-mist">
		<div class="text-center max-w-md mx-auto px-8">
			<div
				class="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6"
			>
				<svg
					class="w-12 h-12 text-red-500"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
					/>
				</svg>
			</div>
			<h2 class="text-3xl font-cenzo font-bold text-primary mb-4">{{ errorMessage() }}</h2>
			<p class="text-charcoal/60 mb-8">
				The blog post you're looking for doesn't exist or has been removed.
			</p>
			<a
				routerLink="/blog"
				class="inline-block px-8 py-3 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition-all"
			>
				Back to Blog
			</a>
		</div>
	</div>
}

<!-- Blog Content -->
@else if (blog()) {
	<article class="min-h-screen bg-white">
		<!-- Hero Section -->
		<div class="relative h-[75vh] overflow-hidden">
			<img
				[src]="getImageUrl(blog()?.featured_image_url || '')"
				[alt]="blog()?.title || ''"
				class="w-full h-full object-cover"
				(error)="onImageError($event)"
			/>
			<div
				class="absolute inset-0 bg-gradient-to-b from-primary to-secondary opacity-60"
			></div>
			<div
				class="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/60 to-transparent"
			></div>

			<!-- Hero Content -->
			<div class="absolute bottom-0 left-0 right-0 p-8 md:p-16 text-white">
				<div class="max-w-4xl mx-auto">
					<!-- Tags -->
					@if ((blog()?.tags?.length ?? 0) > 0) {
						<div class="flex flex-wrap gap-2 mb-4">
							@for (tag of blog()?.tags ?? []; track tag.id) {
								<span
									class="px-3 py-1 bg-secondary/80 backdrop-blur-sm rounded-full text-xs font-semibold"
								>
									{{ tag.name }}
								</span>
							}
						</div>
					}

					<!-- Title -->
					<h1
						class="font-cenzo text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
					>
						{{ blog()?.title }}
					</h1>

					<!-- Meta Info -->
					<div class="flex flex-wrap items-center gap-4 text-sm text-white/80">
						<div class="flex items-center gap-2">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
								/>
							</svg>
							<span>{{
								formatDate(blog()?.published_at || blog()?.created_at || '')
							}}</span>
						</div>
						<div class="flex items-center gap-2">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
							<span>{{ blog()?.reading_time_minutes }} min read</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Main Content -->
		<div class="max-w-4xl mx-auto px-8 md:px-16 py-12">
			<!-- Excerpt -->
			@if (blog()?.short_description) {
				<div
					class="bg-gradient-to-r from-secondary/10 to-caramel/10 border-l-4 border-secondary p-6 rounded-r-xl mb-12"
				>
					<p class="text-lg text-charcoal/80 italic">{{ blog()?.short_description }}</p>
				</div>
			}

			<!-- Content -->
			<div
				class="prose prose-lg max-w-none prose-headings:font-cenzo prose-headings:text-primary prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl prose-p:text-charcoal/80 prose-p:leading-relaxed prose-a:text-secondary prose-a:no-underline hover:prose-a:underline prose-strong:text-primary prose-strong:font-semibold prose-img:rounded-xl prose-img:shadow-lg prose-blockquote:border-l-4 prose-blockquote:border-secondary prose-blockquote:bg-mist prose-blockquote:p-4 prose-blockquote:rounded-r-lg prose-code:text-primary prose-code:bg-mist prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-charcoal prose-pre:text-white prose-ul:list-disc prose-ol:list-decimal prose-li:text-charcoal/80"
				[innerHTML]="getSafeHtml(blog()?.content || '')"
			></div>

			<!-- Share Section -->
			<div class="mt-16 pt-8 border-t border-charcoal/10">
				<h3 class="font-cenzo text-xl font-bold text-primary mb-4">Share this article</h3>
				<div class="flex gap-3">
					<button
						(click)="shareOn('facebook')"
						class="w-12 h-12 bg-mist hover:bg-primary text-charcoal hover:text-white rounded-full flex items-center justify-center transition-all cursor-pointer"
						title="Share on Facebook"
					>
						<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
							<path
								d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
							/>
						</svg>
					</button>
					<button
						(click)="shareOn('twitter')"
						class="w-12 h-12 bg-mist hover:bg-primary text-charcoal hover:text-white rounded-full flex items-center justify-center transition-all cursor-pointer"
						title="Share on X"
					>
						<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
							<path
								d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
							/>
						</svg>
					</button>
					<button
						(click)="shareOn('linkedin')"
						class="w-12 h-12 bg-mist hover:bg-primary text-charcoal hover:text-white rounded-full flex items-center justify-center transition-all cursor-pointer"
						title="Share on LinkedIn"
					>
						<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
							<path
								d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
							/>
						</svg>
					</button>
					<button
						(click)="shareOn('whatsapp')"
						class="w-12 h-12 bg-mist hover:bg-primary text-charcoal hover:text-white rounded-full flex items-center justify-center transition-all cursor-pointer"
						title="Share on WhatsApp"
					>
						<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
							<path
								d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
							/>
						</svg>
					</button>
					<button
						(click)="shareOn('instagram')"
						class="w-12 h-12 bg-mist hover:bg-primary text-charcoal hover:text-white rounded-full flex items-center justify-center transition-all cursor-pointer"
						title="Share on Instagram"
					>
						<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
							<path
								d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
							/>
						</svg>
					</button>
					<button
						(click)="copyLink()"
						class="w-12 h-12 rounded-full flex items-center justify-center transition-all cursor-pointer"
						[class.bg-green-100]="linkCopied()"
						[class.text-green-600]="linkCopied()"
						[class.bg-mist]="!linkCopied()"
						[class.text-charcoal]="!linkCopied()"
						[class.hover:bg-primary]="!linkCopied()"
						[class.hover:text-white]="!linkCopied()"
						[title]="linkCopied() ? 'Copied!' : 'Copy link'"
					>
						@if (linkCopied()) {
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M5 13l4 4L19 7"
								/>
							</svg>
						} @else {
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
								/>
							</svg>
						}
					</button>
				</div>
			</div>
		</div>

		<!-- Related Posts -->
		@if (relatedBlogs().length > 0) {
			<div class="bg-mist py-16 px-8 md:px-16">
				<div class="max-w-7xl mx-auto">
					<h2 class="font-cenzo text-3xl md:text-4xl font-bold text-primary mb-8">
						Related Articles
					</h2>
					<div class="grid grid-cols-1 md:grid-cols-3 gap-8">
						@for (relatedBlog of relatedBlogs(); track relatedBlog.id) {
							<a [routerLink]="['/blog', relatedBlog.slug]" class="group block">
								<div
									class="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
								>
									<div class="relative h-48 overflow-hidden">
										<img
											[src]="getImageUrl(relatedBlog.featured_image_url)"
											[alt]="relatedBlog.title"
											class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
											(error)="onImageError($event)"
										/>
									</div>
									<div class="p-6">
										<h3
											class="font-cenzo text-xl font-bold text-primary mb-2 line-clamp-2 group-hover:text-secondary transition-colors"
										>
											{{ relatedBlog.title }}
										</h3>
										<p class="text-charcoal/70 text-sm line-clamp-2 mb-3">
											{{ relatedBlog.short_description }}
										</p>
										<div class="flex items-center text-xs text-charcoal/50">
											<span>{{
												formatDate(
													relatedBlog.published_at ||
														relatedBlog.created_at ||
														''
												)
											}}</span>
											<span class="mx-2">•</span>
											<span
												>{{ relatedBlog.reading_time_minutes }} min
												read</span
											>
										</div>
									</div>
								</div>
							</a>
						}
					</div>
				</div>
			</div>
		}
	</article>
}
